import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../api';
import { Spinner, Alert } from '../../components/UI';

// jsPDF is ~130 kB gzipped, so it is only fetched when a card is actually asked for.
async function makeProgressCard(data) {
  const { downloadProgressCard } = await import('../../utils/progressCard');
  await downloadProgressCard(data);
}

export default function UserActivityAttempt() {
  const { userId, attemptId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | correct | incorrect
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    api.get(`/reports/user-activity/${userId}/attempts/${attemptId}`).then(setData).catch(e => setError(e.message));
  }, [userId, attemptId]);

  if (!data && !error) return <Spinner />;
  if (error) return <div className="content-narrow"><Alert type="danger">{error}</Alert></div>;

  const pct = data.max_score > 0 ? ((data.total_score / data.max_score) * 100).toFixed(1) : '0.0';

  // Full marks counts as correct; anything short of that (including partial credit
  // on an AI-scored text answer) counts as incorrect.
  const isCorrect = a => Number(a.awarded_score) >= Number(a.max_score);
  // Trim float noise (0.6666666666 -> 0.67) but keep whole numbers clean (2 -> "2").
  const fmtScore = n => {
    const num = Number(n);
    return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '');
  };
  const correctCount = data.answers.filter(isCorrect).length;
  const incorrectCount = data.answers.length - correctCount;
  const visibleAnswers = data.answers.filter(a =>
    filter === 'all' || (filter === 'correct' ? isCorrect(a) : !isCorrect(a))
  );
  // Every row on the Correct tab already shows the right answer under "Your answer".
  const showAnswerCol = showAnswers && filter !== 'correct';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow"><Link to={`/admin/user-activity/${userId}`}>&larr; {data.user_name}</Link></div>
          <h1>{data.paper_title}</h1>
          <p className="mono text-sm">{data.user_name} &middot; {data.mobile}</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => makeProgressCard(data).catch(e => setError(e.message))}
        >
          Download progress card
        </button>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">Score</div>
          <div className="value">{data.total_score} / {data.max_score}</div>
        </div>
        <div className="stat-card">
          <div className="label">Percentage</div>
          <div className="value">{pct}%</div>
        </div>
        <div className="stat-card">
          <div className="label">Submitted</div>
          <div className="value" style={{ fontSize: 'var(--fs-lg)' }}>
            {data.submitted_at ? new Date(data.submitted_at).toLocaleDateString('en-IN') : '-'}
          </div>
        </div>
      </div>

      <div className="tabs">
        <div className={`tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All {data.answers.length}
        </div>
        <div className={`tab ${filter === 'correct' ? 'active' : ''}`} onClick={() => setFilter('correct')}>
          Correct {correctCount}
        </div>
        <div className={`tab ${filter === 'incorrect' ? 'active' : ''}`} onClick={() => setFilter('incorrect')}>
          Incorrect {incorrectCount}
        </div>
        <div className="checkbox-row" style={{ marginLeft: 'auto', display: filter === 'correct' ? 'none' : undefined }}>
          <input type="checkbox" id="show_answers" checked={showAnswers}
            onChange={e => setShowAnswers(e.target.checked)} />
          <label htmlFor="show_answers" style={{ marginBottom: 0 }}>Show answers</label>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Department</th><th>Question</th><th>Answer given</th>
              {showAnswerCol && <th>Correct Answer</th>}
              <th>Result</th><th>Score</th>
            </tr>
          </thead>
          <tbody>
            {visibleAnswers.map(a => (
              <tr key={a.question_id}>
                <td className="text-sm text-muted">{a.department_name}</td>
                <td>{a.question_text}</td>
                <td className="text-sm">{a.selected_option_text || a.answer_text || '-'}</td>
                {showAnswerCol && (
                  <td className="text-sm">
                    {/* On a correct row this would only repeat what the candidate wrote. */}
                    {isCorrect(a) ? '' : (a.correct_answer_text || <span className="text-muted">Reviewed manually</span>)}
                    {!isCorrect(a) && a.explanation && <div className="text-sm text-muted mt-2">{a.explanation}</div>}
                  </td>
                )}
                <td>
                  <span className={`badge ${isCorrect(a) ? 'badge-success' : 'badge-danger'}`}>
                    {isCorrect(a) ? 'Correct' : 'Incorrect'}
                  </span>
                </td>
                <td className="score">
                  <span className={`score-pill ${
                    Number(a.awarded_score) >= Number(a.max_score) ? 'is-full'
                      : Number(a.awarded_score) > 0 ? 'is-partial' : 'is-zero'
                  }`}>
                    <span className="awarded">{fmtScore(a.awarded_score)}</span>
                    <span className="sep">/</span>
                    <span className="max">{fmtScore(a.max_score)}</span>
                  </span>
                </td>
              </tr>
            ))}
            {visibleAnswers.length === 0 && (
              <tr><td colSpan={showAnswerCol ? 6 : 5} className="text-sm text-muted">
                {filter === 'correct' ? 'No questions were answered correctly.' : 'Nothing wrong here — every question was correct.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
